import { pick } from 'lodash';
import React from 'react';
import Modal from 'react-modal';
import { connect, Dispatch } from 'react-redux';
import { InjectedRouter } from 'react-router';
import { bindActionCreators } from 'redux';

import { withRouter } from 'app/context/Router';
import CurrentUser from 'auth/CurrentUser';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import ConfirmDialog from 'components/ConfirmDialog';
import Dialog from 'components/Dialog';
import { Message, Target } from 'entities/Message';
import EventTracking from 'lib/EventTracking';
import { clearSelection, saveEnvelope, Envelope } from 'reducers/roleBasedMessagingSearch';

import { ComposerContext, UIEventContext } from './ComposerContext';
import SplitComposer, { MessageType } from './SplitComposer';
import { ComposerStateContext, withComposerDispatch, ComposerDispatch } from 'chat_v2/ComposerContext';

type Props = {
  skipSelectRecipients?: boolean;
  messageType: MessageType;
  onRequestHide: () => void;
  router: InjectedRouter;
  onComposerPage: boolean;
  draftAnnouncement?: Message;
  initialTargets?: Target[];
  onMessageSent?: (options: { scheduledTime?: Date; targets: Target[] }) => void;
  composerDispatch: ComposerDispatch;
};

type ReduxDispatchProps = {
  clearSelection: () => void;
  saveEnvelope: (x: Envelope) => void;
};

type DefaultProps = {
  messageType: MessageType;
  onRequestHide: () => void;
};

type State = {
  isOpen: boolean;
  // Controls whether to show the "Discard selection?" modal on cancel
  significantChange: boolean;
  // Set when the user clicks "Continue" to move to the composer,
  // so we don't treat it as a cancel action in handleRequestHide
  isContinue: boolean;
};

class SplitComposerModal extends React.Component<Props & ReduxDispatchProps, State> {
  // messageType is optional as a prop but is required in redux, so default it here
  static defaultProps: DefaultProps = { messageType: 'message', onRequestHide: () => {} };

  state: State = {
    isOpen: false,
    significantChange: false,
    isContinue: false,
  };

  handleConfirm = () => {
    this.props.clearSelection();
    this.props.composerDispatch({ type: 'removeAllRecipients' });
    this.props.onRequestHide();
  };
  handleCancel = () => this.setState({ isOpen: false });

  generateHandleRequestHide = (uiEventContext: UIEventContext) => () => {
    if (!this.state.isContinue && !this.props.onComposerPage) {
      EventTracking.trackKey(`announcements.share_to.cancel.click`, uiEventContext);

      if (this.state.significantChange) {
        this.setState({ isOpen: true });
        return;
      }
    }
    this.props.onRequestHide();
  };

  handleSignificantChange = () => {
    if (!this.state.significantChange) {
      this.setState({ significantChange: true });
    }
  };

  handlePageChange = () => {
    this.setState({ isContinue: true });
    this.props.saveEnvelope(
      pick(this.props, ['draftAnnouncement', 'initialTargets', 'messageType', 'onMessageSent'])
    );
    this.props.onRequestHide();
    this.props.router.push('/compose');
  };

  componentDidMount() {
    if (this.props.skipSelectRecipients) this.handlePageChange();

    // This means that the user _switched_ to an urgent message (vs. editing recipients from
    // the "To:" bar on the full page composer.) We should clear the selected recipients in
    // this case since not all targets can receive urgent messages.
    if (this.props.messageType === 'urgent' && !this.props.onComposerPage) {
      this.props.clearSelection();
      this.props.composerDispatch({ type: 'removeAllRecipients' });
    }
  }

  render() {
    return this.props.skipSelectRecipients ? null : (
      <React.Fragment>
        <Modal isOpen={this.state.isOpen} data-test="confirm-close-dialog">
          <ConfirmDialog
            hideCloseButton
            cancelButtonText="Cancel"
            actionButtonText="Discard"
            onCancel={this.handleCancel}
            onConfirm={this.handleConfirm}
            onRequestHide={this.handleCancel}
            title="Discard selection?"
          />
        </Modal>
        <ComposerContext.Consumer>
          {composerContext => {
            if (!CurrentUser.isSignedIn()) {
              return null;
            }

            return (
              <Dialog
                size="large"
                className="SplitComposerModal"
                onRequestHide={this.generateHandleRequestHide(composerContext.getUIEventContext())}
                box={false /* a boxed dialog has bigger font size than a box in a dialog */}>
                <Box>
                  <BoxBody full>
                    <ComposerStateContext.Consumer>
                      {composerState => (
                        <SplitComposer
                          {...this.props}
                          onPageChange={this.props.onComposerPage ? this.props.onRequestHide : this.handlePageChange}
                          onSignificantChangeMade={this.handleSignificantChange}
                          recipients={{...composerState.recipients}}
                          filters={{...composerState.filters}}
                          clearSelection={() => this.props.composerDispatch({ type: 'removeAllRecipients' })}
                        />
                      )}
                    </ComposerStateContext.Consumer>
                  </BoxBody>
                </Box>
              </Dialog>
            );
          }}
        </ComposerContext.Consumer>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps =>
  bindActionCreators({ clearSelection, saveEnvelope }, dispatch);

export { SplitComposerModal };
export default withComposerDispatch(withRouter(connect(undefined, mapDispatchToProps)(SplitComposerModal)));
